import { ReactElement, useEffect, useState } from "react"
import { useLocation, useNavigate, useParams } from "react-router-dom"
import { Loading, Tab, Tabs } from "@wfp/ui"
import { HH_MODULE } from "constants/constants"
import { useAppSelector } from "hooks/hooksStore"
import { selectUser } from "features/common/commonSlice"
import { useGetBaseXlsHHQuery } from "services/household/xlsFormSchemas"
import { checkAccess } from "utils/helpers"
import ErrorState from "components/Shared/EmptyState/ErrorState"
import BaseXlsFormDetails from "components/Household/BaseXlsForm/BaseXlsFormDetails"
import BaseXlsFormList from "components/Household/BaseXlsForm/BaseXlsFormList"
import BaseXlsFormData from "components/Household/BaseXlsForm/BaseXlsFormData"
import BaseXlsFormChoiceList from "components/Household/BaseXlsForm/BaseXlsFormChoiceList"
import BaseXlsFormLinked from "components/Household/BaseXlsForm/BaseXlsFormLinked"

const BaseXlsFormPage = (): ReactElement => {
  const navigate = useNavigate()
  const { search, hash } = useLocation()

  const mode = new URLSearchParams(search)?.get("mode") ?? "view"
  const { baseXlsFormId } = useParams<{ baseXlsFormId: string }>()
  const user = useAppSelector(selectUser)

  const [permissions, setPermissions] = useState<boolean>(false)
  const [isValid, setIsValid] = useState<boolean>(false)

  const {
    data: baseXls,
    isLoading: isLoadingBaseXls,
    error: errorBaseXls,
  } = useGetBaseXlsHHQuery(Number(baseXlsFormId), { skip: !baseXlsFormId })

  const selectTab = (): number => {
    switch (hash) {
      case "#details":
        return 0
      case "#list":
        return 1
      case "#choice-list":
        return 2
      case "#export":
        return isValid ? 3 : 1
      case "#linked-forms":
        return 4
      default:
        return 0
    }
  }

  const changeMode = (): void => {
    if (mode === "edit") {
      navigate(`/household/baseXlsFormSchema/${baseXlsFormId}#details`)
    } else {
      navigate(`/household/baseXlsFormSchema/${baseXlsFormId}?mode=edit#details`)
    }
  }

  useEffect(() => {
    if (baseXls) {
      const check = checkAccess(user?.roles, [HH_MODULE], "baseXLSForms", "edit")
      setPermissions(check)
      setIsValid(baseXls.isValid)
      !check && mode === "edit" && changeMode()
    }
  }, [baseXls, user])

  if (isLoadingBaseXls) return <Loading small />
  if (errorBaseXls) return <ErrorState />

  return (
    <Tabs selected={selectTab()} disableAnimation>
      <Tab label="Details" href="" role="tab" selected tabIndex={0} onClick={() => navigate(`#details`)}>
        {baseXls && <BaseXlsFormDetails data={baseXls} mode={mode} changeMode={changeMode} />}
      </Tab>

      {isValid && (
        <Tab label="Field list" href="" role="tab" selected={false} tabIndex={0} onClick={() => navigate(`#list`)}>
          <BaseXlsFormList xlsFormId={Number(baseXlsFormId)} permissions={permissions} />
        </Tab>
      )}

      {isValid && (
        <Tab
          label="Choice list"
          href=""
          role="tab"
          selected={false}
          tabIndex={0}
          onClick={() => navigate(`#choice-list`)}
        >
          <BaseXlsFormChoiceList xlsFormId={Number(baseXlsFormId)} permissions={permissions} />
        </Tab>
      )}

      <Tab label="Export" href="" role="tab" selected={false} tabIndex={0} onClick={() => navigate(`#export`)}>
        {baseXls && <BaseXlsFormData baseXls={baseXls} />}
      </Tab>

      {isValid && (
        <Tab
          label="Linked forms"
          href=""
          role="tab"
          selected={false}
          tabIndex={0}
          onClick={() => navigate(`#linked-forms`)}
        >
          <BaseXlsFormLinked xlsFormId={Number(baseXlsFormId)} />
        </Tab>
      )}
    </Tabs>
  )
}

export default BaseXlsFormPage
